/**
 * Nexus Imports
 */
import { Dispatch, FC, SetStateAction, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Internal Imports
 */
import { Dropdown } from '../../shared/dropdowns';

import { useGetMasterTemplatesQuery } from '../../../redux/apis/Templates.api';
import supportRoundingTypes from '../../../constants/supportRoundingTypes.json';

/**
 * Global Type Definition Imports
 */
import {
  ICondensedMasterTemplate,
  IFullTemplate
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import {
  Container,
  SelectTemplateHeader
} from './SelectTemplatesDropdown.styles';

interface SelectTemplateDropDownProps {
  roundingType?: string;
  selectedMasterTemplate?: ICondensedMasterTemplate | IFullTemplate | null;
  setSelectedMasterTemplate?: Dispatch<
    SetStateAction<ICondensedMasterTemplate | IFullTemplate | null>
  >;
  type: string;
}

const SelectTemplateDropDown: FC<SelectTemplateDropDownProps> = ({
  roundingType,
  selectedMasterTemplate,
  setSelectedMasterTemplate = () => {},
  type
}) => {
  const [selectedTemplateName, setSelectedTemplateName] = useState<string>(
    type === 'questionBank' ? 'All' : ''
  );
  const [filteredMasterTemplateList, setFilteredMasterTemplateList] = useState<
    (ICondensedMasterTemplate | IFullTemplate)[]
  >([]);
  const [open, setOpen] = useState(false);
  const [rendered, setRendered] = useState<boolean>(false);

  const navigate = useNavigate();

  const {
    data: masterTemplateData
    // isError: isGetMasterTemplatesError,
    // isFetching: isGetMasterTemplatesFetching,
    // isLoading: isGetMasterTemplatesLoading
  } = useGetMasterTemplatesQuery(
    { archived: false, condensed: false },
    {
      refetchOnMountOrArgChange: 300
    }
  );

  const onChangeTemplate = (value: string) => {
    switch (type) {
      case 'questionBank': {
        if (value === 'all') {
          setSelectedTemplateName('All');
          navigate('/admin/question-bank/all');
        } else {
          const newSelectedTemplate = filteredMasterTemplateList.find(
            (template: ICondensedMasterTemplate | IFullTemplate) =>
              template.templateConfigId === value
          );
          setSelectedTemplateName(newSelectedTemplate?.name ?? '');

          if (newSelectedTemplate)
            navigate(
              `/admin/question-bank/${newSelectedTemplate?.templateConfigId}`
            );
        }
        break;
      }
      case 'templateTree': {
        const newSelectedTemplate = filteredMasterTemplateList.find(
          (template: ICondensedMasterTemplate | IFullTemplate) =>
            template.templateConfigId === value
        );
        if (newSelectedTemplate) setSelectedMasterTemplate(newSelectedTemplate);
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    setRendered(true);
  }, []);

  useEffect(() => {
    if (type === 'templateTree' && rendered) setSelectedMasterTemplate(null);
  }, [roundingType]);

  useEffect(() => {
    if (roundingType) {
      const isSupportRoundingType = !(
        roundingType === 'patient' ||
        roundingType === 'employee' ||
        supportRoundingTypes.validation.includes(roundingType)
      );
      const filtered = isSupportRoundingType
        ? masterTemplateData?.filter(
            (template: ICondensedMasterTemplate | IFullTemplate) =>
              supportRoundingTypes.support.includes(template.roundingType)
          ) ?? []
        : masterTemplateData?.filter(
            (template: ICondensedMasterTemplate | IFullTemplate) =>
              template.roundingType === roundingType
          ) ?? [];
      setFilteredMasterTemplateList(filtered);
    } else {
      setFilteredMasterTemplateList(masterTemplateData ?? []);
    }
  }, [masterTemplateData, roundingType]);

  useEffect(() => {
    switch (type) {
      case 'templateTree': {
        if (filteredMasterTemplateList?.length > 0) {
          if (!selectedMasterTemplate) {
            setSelectedTemplateName(filteredMasterTemplateList[0]?.name || '');
            setSelectedMasterTemplate(filteredMasterTemplateList[0]);
          } else {
            const newlySelectedTemplate = filteredMasterTemplateList.find(
              (template: ICondensedMasterTemplate | IFullTemplate) =>
                template.templateConfigId ===
                selectedMasterTemplate.templateConfigId
            );
            setSelectedTemplateName(newlySelectedTemplate?.name ?? '');
          }
        }
        break;
      }
      case 'questionBank': {
        break;
      }
      default: {
        console.log('type not recognized');
      }
    }
  }, [filteredMasterTemplateList, selectedMasterTemplate]);

  return (
    <Container>
      <SelectTemplateHeader className="mb-0 pe-3">
        Template
      </SelectTemplateHeader>
      <Dropdown
        classes="ps-0"
        open={open}
        placeholder={selectedTemplateName || ''}
        setOpen={setOpen}
      >
        {type === 'questionBank' ? (
          <div
            key="templates-all"
            aria-selected={selectedTemplateName === 'all'}
            className={`dropdown-hover-item${
              selectedTemplateName === 'all' ? ' selected' : ''
            }`}
            role="option"
            tabIndex={0}
            onClick={() => onChangeTemplate('all')}
            onKeyDown={() => onChangeTemplate('all')}
          >
            All
          </div>
        ) : null}
        {filteredMasterTemplateList.map(
          (template: ICondensedMasterTemplate | IFullTemplate) => {
            return (
              <div
                key={template.templateConfigId}
                aria-selected={selectedTemplateName === template.name}
                className={`dropdown-hover-item${
                  selectedTemplateName === template.name ? ' selected' : ''
                }`}
                role="option"
                tabIndex={0}
                onClick={() => onChangeTemplate(template.templateConfigId)}
                onKeyDown={() => onChangeTemplate(template.templateConfigId)}
              >
                {template.name}
              </div>
            );
          }
        )}
      </Dropdown>
    </Container>
  );
};

export default SelectTemplateDropDown;
